/**
 * Workflow Templates — Pre-defined workflow patterns for drug discovery.
 *
 * Encodes the 7 workflow composition patterns from the drug discovery task
 * catalog as declarative template data structures. Each template can be
 * instantiated into a Workflow DAG via `createFromTemplate()`.
 *
 * Templates are plain JavaScript objects (JSON-serializable) containing:
 * - `id` — unique kebab-case identifier
 * - `name` — human-readable display name
 * - `description` — what this workflow accomplishes
 * - `category` — grouping (e.g., "Lead Optimization", "Fragment Design")
 * - `tags` — searchable keywords
 * - `steps` — ordered array of node definitions with actionId + param defaults
 * - `connections` — edges between steps (data flow)
 *
 * @module gibbs/workflow/templates
 */

import { Workflow } from './Workflow';
import { getAction } from '../registry/index';

// ─── Template Definitions ───────────────────────────────────────────────────

/**
 * @typedef {Object} TemplateStep
 * @property {string} actionId - ActionDef ID from registry
 * @property {Object} [paramDefaults] - Default parameter overrides for this step
 * @property {{ x: number, y: number }} [position] - UI position hint
 * @property {string} [label] - Custom label (overrides ActionDef name)
 */

/**
 * @typedef {Object} TemplateConnection
 * @property {number} fromStep - Index into the steps array (source node)
 * @property {number} toStep - Index into the steps array (target node)
 * @property {string} outputName - Output name from source ActionDef
 * @property {string} inputName - Input name on target ActionDef
 */

/**
 * @typedef {Object} WorkflowTemplate
 * @property {string} id - Unique template identifier
 * @property {string} name - Human-readable name
 * @property {string} description - What this workflow accomplishes
 * @property {string} category - Grouping category
 * @property {string[]} tags - Searchable keywords
 * @property {TemplateStep[]} steps - Ordered node definitions
 * @property {TemplateConnection[]} connections - Data flow edges between steps
 */

/** @type {WorkflowTemplate[]} */
const templates = [
    // ─── Pattern 1: Hit-to-Lead Optimization ────────────────────────────
    {
        id: 'hit-to-lead',
        name: 'Hit-to-Lead Optimization',
        description: 'Load a protein and compound, dock, score, grow fragments, minimize, and predict ADMET properties. The canonical lead optimization pipeline.',
        category: 'Lead Optimization',
        tags: ['docking', 'grow', 'minimize', 'admet', 'lead-optimization'],
        steps: [
            {
                actionId: 'load-protein-from-pdb', paramDefaults: {}, position: { x: 0, y: 0 }, label: 'Load Protein',
            },
            {
                actionId: 'load-compound', paramDefaults: {}, position: { x: 200, y: 0 }, label: 'Load Compound',
            },
            {
                actionId: 'dock-compound', paramDefaults: {}, position: { x: 400, y: 0 }, label: 'Dock',
            },
            {
                actionId: 'get-energies', paramDefaults: {}, position: { x: 600, y: 0 }, label: 'Score Energies',
            },
            {
                actionId: 'grow-from-atom', paramDefaults: {}, position: { x: 800, y: 0 }, label: 'Grow Fragment',
            },
            {
                actionId: 'confirm-modification', paramDefaults: {}, position: { x: 1000, y: 0 }, label: 'Confirm Grow',
            },
            {
                actionId: 'energy-minimize', paramDefaults: {}, position: { x: 1200, y: 0 }, label: 'Minimize',
            },
            {
                actionId: 'get-energies', paramDefaults: {}, position: { x: 1400, y: 0 }, label: 'Re-score Energies',
            },
            {
                actionId: 'predict-admet', paramDefaults: {}, position: { x: 1600, y: 0 }, label: 'Predict ADMET',
            },
            {
                actionId: 'export-compounds', paramDefaults: {}, position: { x: 1800, y: 0 }, label: 'Export',
            },
        ],
        connections: [
            {
                fromStep: 0, toStep: 1, outputName: 'caseData', inputName: 'caseData',
            },
            {
                fromStep: 1, toStep: 2, outputName: 'compounds', inputName: 'compounds',
            },
            {
                fromStep: 2, toStep: 3, outputName: 'dockedPoses', inputName: 'compounds',
            },
            {
                fromStep: 3, toStep: 4, outputName: 'energies', inputName: 'bondVectorPair',
            },
            {
                fromStep: 4, toStep: 5, outputName: 'suggestions', inputName: 'suggestion',
            },
            {
                fromStep: 5, toStep: 6, outputName: 'compound', inputName: 'compounds',
            },
            {
                fromStep: 6, toStep: 7, outputName: 'minimizedCompounds', inputName: 'compounds',
            },
            {
                fromStep: 7, toStep: 8, outputName: 'energies', inputName: 'compounds',
            },
            {
                fromStep: 8, toStep: 9, outputName: 'predictions', inputName: 'compounds',
            },
        ],
    },

    // ─── Pattern 2: Fragment Search with Property Filtering ─────────────
    {
        id: 'fragment-search-filtered',
        name: 'Fragment Search with Property Filtering',
        description: 'Load a BMaps protein map with pre-computed fragments, load starters, grow fragments, confirm, minimize, and predict ADMET. Focused on fragment-based drug design.',
        category: 'Fragment Design',
        tags: ['fragment', 'grow', 'filter', 'admet', 'fbdd'],
        steps: [
            {
                actionId: 'load-protein-from-map', paramDefaults: {}, position: { x: 0, y: 0 }, label: 'Load Protein Map',
            },
            {
                actionId: 'load-starter-compounds', paramDefaults: {}, position: { x: 200, y: 0 }, label: 'Load Starters',
            },
            {
                actionId: 'grow-from-atom', paramDefaults: {}, position: { x: 400, y: 0 }, label: 'Grow Fragment',
            },
            {
                actionId: 'confirm-modification', paramDefaults: {}, position: { x: 600, y: 0 }, label: 'Confirm Grow',
            },
            {
                actionId: 'energy-minimize', paramDefaults: {}, position: { x: 800, y: 0 }, label: 'Minimize',
            },
            {
                actionId: 'predict-admet', paramDefaults: {}, position: { x: 1000, y: 0 }, label: 'Predict ADMET',
            },
        ],
        connections: [
            {
                fromStep: 0, toStep: 1, outputName: 'caseData', inputName: 'caseData',
            },
            {
                fromStep: 1, toStep: 2, outputName: 'compounds', inputName: 'bondVectorPair',
            },
            {
                fromStep: 2, toStep: 3, outputName: 'suggestions', inputName: 'suggestion',
            },
            {
                fromStep: 3, toStep: 4, outputName: 'compound', inputName: 'compounds',
            },
            {
                fromStep: 4, toStep: 5, outputName: 'minimizedCompounds', inputName: 'compounds',
            },
        ],
    },

    // ─── Pattern 3: Selectivity Analysis ────────────────────────────────
    {
        id: 'selectivity-analysis',
        name: 'Selectivity Analysis',
        description: 'Load target and off-target proteins, align structures, score a compound against both, generate interaction fingerprints (ProLIF), and export. Parallel branch structure for multi-protein comparison.',
        category: 'Selectivity',
        tags: ['selectivity', 'multi-protein', 'align', 'prolif', 'scoring'],
        steps: [
            {
                actionId: 'load-protein-from-pdb', paramDefaults: {}, position: { x: 0, y: -100 }, label: 'Load Target',
            },
            {
                actionId: 'load-protein-from-pdb', paramDefaults: { keepExisting: true }, position: { x: 0, y: 100 }, label: 'Load Off-Target',
            },
            {
                actionId: 'align-protein', paramDefaults: {}, position: { x: 200, y: 0 }, label: 'Align Structures',
            },
            {
                actionId: 'set-protein-target', paramDefaults: {}, position: { x: 400, y: 0 }, label: 'Set Target',
            },
            {
                actionId: 'load-compound', paramDefaults: {}, position: { x: 600, y: 0 }, label: 'Load Compound',
            },
            {
                actionId: 'score-compounds', paramDefaults: {}, position: { x: 800, y: 0 }, label: 'Score Multi-Protein',
            },
            {
                actionId: 'fetch-prolif-data', paramDefaults: {}, position: { x: 1000, y: 0 }, label: 'ProLIF Interactions',
            },
            {
                actionId: 'export-compounds', paramDefaults: {}, position: { x: 1200, y: 0 }, label: 'Export',
            },
        ],
        connections: [
            // Both proteins feed into alignment
            {
                fromStep: 0, toStep: 2, outputName: 'caseData', inputName: 'refCaseData',
            },
            {
                fromStep: 1, toStep: 2, outputName: 'caseData', inputName: 'alignCaseData',
            },
            // Alignment → set target → load compound → score
            {
                fromStep: 0, toStep: 3, outputName: 'caseData', inputName: 'mapCase',
            },
            {
                fromStep: 0, toStep: 4, outputName: 'caseData', inputName: 'caseData',
            },
            {
                fromStep: 4, toStep: 5, outputName: 'compounds', inputName: 'compounds',
            },
            {
                fromStep: 5, toStep: 6, outputName: 'results', inputName: 'compound',
            },
            {
                fromStep: 4, toStep: 7, outputName: 'compounds', inputName: 'compounds',
            },
        ],
    },

    // ─── Pattern 4: De Novo Generation + Screening ──────────────────────
    {
        id: 'de-novo-generation',
        name: 'De Novo Generation + Screening',
        description: 'Load protein and seed compound, run REINVENT generative design to create novel molecules, screen the generated library, predict ADMET, and export hits.',
        category: 'Generative Design',
        tags: ['reinvent', 'generative', 'de-novo', 'screening', 'ai'],
        steps: [
            {
                actionId: 'load-protein-from-pdb', paramDefaults: {}, position: { x: 0, y: 0 }, label: 'Load Protein',
            },
            {
                actionId: 'load-compound', paramDefaults: {}, position: { x: 200, y: 0 }, label: 'Load Seed Compound',
            },
            {
                actionId: 'job-reinvent', paramDefaults: {}, position: { x: 400, y: 0 }, label: 'REINVENT Generate',
            },
            {
                actionId: 'job-library-screen', paramDefaults: {}, position: { x: 600, y: 0 }, label: 'Library Screen',
            },
            {
                actionId: 'predict-admet', paramDefaults: {}, position: { x: 800, y: 0 }, label: 'Predict ADMET',
            },
            {
                actionId: 'export-compounds', paramDefaults: {}, position: { x: 1000, y: 0 }, label: 'Export',
            },
        ],
        connections: [
            {
                fromStep: 0, toStep: 1, outputName: 'caseData', inputName: 'caseData',
            },
            {
                fromStep: 1, toStep: 2, outputName: 'compounds', inputName: 'smilesList',
            },
            {
                fromStep: 2, toStep: 3, outputName: 'generatedMolecules', inputName: 'compounds',
            },
            {
                fromStep: 3, toStep: 4, outputName: 'results', inputName: 'compounds',
            },
            {
                fromStep: 4, toStep: 5, outputName: 'predictions', inputName: 'compounds',
            },
        ],
    },

    // ─── Pattern 5: AI Structure Prediction + Fragment Design ───────────
    {
        id: 'ai-folding-design',
        name: 'AI Structure Prediction + Fragment Design',
        description: 'Predict a protein structure with AI folding, load the fragment map, create cluster hotspot map, grow fragments, confirm, minimize, predict ADMET, and export.',
        category: 'Structure Prediction',
        tags: ['folding', 'boltz', 'chai', 'fragment', 'cluster', 'ai'],
        steps: [
            {
                actionId: 'fold-protein', paramDefaults: {}, position: { x: 0, y: 0 }, label: 'Fold Protein',
            },
            {
                actionId: 'load-fragment-map', paramDefaults: {}, position: { x: 200, y: 0 }, label: 'Load Fragment Map',
            },
            {
                actionId: 'make-cluster-map', paramDefaults: {}, position: { x: 400, y: 0 }, label: 'Make Cluster Map',
            },
            {
                actionId: 'grow-from-atom', paramDefaults: {}, position: { x: 600, y: 0 }, label: 'Grow Fragment',
            },
            {
                actionId: 'confirm-modification', paramDefaults: {}, position: { x: 800, y: 0 }, label: 'Confirm Grow',
            },
            {
                actionId: 'energy-minimize', paramDefaults: {}, position: { x: 1000, y: 0 }, label: 'Minimize',
            },
            {
                actionId: 'predict-admet', paramDefaults: {}, position: { x: 1200, y: 0 }, label: 'Predict ADMET',
            },
            {
                actionId: 'export-compounds', paramDefaults: {}, position: { x: 1400, y: 0 }, label: 'Export',
            },
        ],
        connections: [
            {
                fromStep: 0, toStep: 1, outputName: 'caseData', inputName: 'fragmentInfo',
            },
            {
                fromStep: 1, toStep: 2, outputName: 'fragmentMap', inputName: 'hotspotFragments',
            },
            {
                fromStep: 2, toStep: 3, outputName: 'clusterMap', inputName: 'bondVectorPair',
            },
            {
                fromStep: 3, toStep: 4, outputName: 'suggestions', inputName: 'suggestion',
            },
            {
                fromStep: 4, toStep: 5, outputName: 'compound', inputName: 'compounds',
            },
            {
                fromStep: 5, toStep: 6, outputName: 'minimizedCompounds', inputName: 'compounds',
            },
            {
                fromStep: 6, toStep: 7, outputName: 'predictions', inputName: 'compounds',
            },
        ],
    },

    // ─── Pattern 6: Building Block Library Search (Planned — Aim 1+2) ───
    {
        id: 'building-block-search',
        name: 'Building Block Library Search',
        description: 'Full building-block pipeline: load protein map, grow from starters, apply reaction-aware search, filter by reaction and properties, assemble with reaction tracking, check vendor availability, minimize, predict ADMET, and export. Requires planned Aim 1 + 2 actions.',
        category: 'Building Block Design',
        tags: ['building-block', 'reaction', 'vendor', 'aim1', 'aim2', 'planned'],
        steps: [
            {
                actionId: 'load-protein-from-map', paramDefaults: {}, position: { x: 0, y: 0 }, label: 'Load Protein Map',
            },
            {
                actionId: 'load-starter-compounds', paramDefaults: {}, position: { x: 200, y: 0 }, label: 'Load Starters',
            },
            {
                actionId: 'grow-from-atom', paramDefaults: {}, position: { x: 400, y: 0 }, label: 'Grow Fragment',
            },
            {
                actionId: 'reaction-aware-fragment-search', paramDefaults: {}, position: { x: 600, y: 0 }, label: 'Reaction-Aware Search',
            },
            {
                actionId: 'filter-by-reaction', paramDefaults: {}, position: { x: 800, y: 0 }, label: 'Filter by Reaction',
            },
            {
                actionId: 'filter-by-properties', paramDefaults: {}, position: { x: 1000, y: 0 }, label: 'Filter by Properties',
            },
            {
                actionId: 'assemble-compound-with-reaction', paramDefaults: {}, position: { x: 1200, y: 0 }, label: 'Assemble with Reaction',
            },
            {
                actionId: 'lookup-building-block-vendor', paramDefaults: {}, position: { x: 1400, y: 0 }, label: 'Vendor Lookup',
            },
            {
                actionId: 'energy-minimize', paramDefaults: {}, position: { x: 1600, y: 0 }, label: 'Minimize',
            },
            {
                actionId: 'predict-admet', paramDefaults: {}, position: { x: 1800, y: 0 }, label: 'Predict ADMET',
            },
            {
                actionId: 'export-compounds', paramDefaults: {}, position: { x: 2000, y: 0 }, label: 'Export',
            },
        ],
        connections: [
            {
                fromStep: 0, toStep: 1, outputName: 'caseData', inputName: 'caseData',
            },
            {
                fromStep: 1, toStep: 2, outputName: 'compounds', inputName: 'bondVectorPair',
            },
            {
                fromStep: 2, toStep: 3, outputName: 'suggestions', inputName: 'suggestions',
            },
            {
                fromStep: 3, toStep: 4, outputName: 'results', inputName: 'suggestions',
            },
            {
                fromStep: 4, toStep: 5, outputName: 'filtered', inputName: 'compounds',
            },
            {
                fromStep: 5, toStep: 6, outputName: 'filtered', inputName: 'modifications',
            },
            {
                fromStep: 6, toStep: 7, outputName: 'compound', inputName: 'compound',
            },
            {
                fromStep: 7, toStep: 8, outputName: 'compound', inputName: 'compounds',
            },
            {
                fromStep: 8, toStep: 9, outputName: 'minimizedCompounds', inputName: 'compounds',
            },
            {
                fromStep: 9, toStep: 10, outputName: 'predictions', inputName: 'compounds',
            },
        ],
    },

    // ─── Pattern 7: Workspace Save/Resume ───────────────────────────────
    {
        id: 'workspace-save-resume',
        name: 'Workspace Save/Resume',
        description: 'Capture the current workspace state for later restoration. Simple single-step workflow for session persistence.',
        category: 'Workspace Management',
        tags: ['save', 'resume', 'state', 'workspace'],
        steps: [
            {
                actionId: 'capture-state', paramDefaults: {}, position: { x: 0, y: 0 }, label: 'Capture State',
            },
        ],
        connections: [],
    },
];

// ─── Template Index ─────────────────────────────────────────────────────────

/** @type {Map<string, WorkflowTemplate>} */
const templateMap = new Map(templates.map((t) => [t.id, t]));

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Get a template definition by its unique ID.
 *
 * @param {string} id - Template identifier (e.g., "hit-to-lead")
 * @returns {WorkflowTemplate | undefined}
 */
export function getTemplate(id) {
    return templateMap.get(id);
}

/**
 * Get all available workflow templates.
 *
 * @returns {WorkflowTemplate[]}
 */
export function getAllTemplates() {
    return Array.from(templateMap.values());
}

/**
 * Instantiate a Workflow from a template definition.
 *
 * Creates real Workflow nodes and edges from the template's declarative
 * step/connection arrays. Optionally applies per-step parameter overrides.
 *
 * @param {string} templateId - Template to instantiate
 * @param {Object} [overrides] - Optional overrides
 * @param {Object<string, { paramOverrides?: Object, label?: string }>} [overrides.nodeOverrides]
 *   Keyed by step reference (e.g., "node-1" for step index 0). Each value
 *   can include `paramOverrides` and/or `label`.
 * @returns {{ workflow: Workflow, warnings: string[] }}
 * @throws {Error} If templateId is not found
 */
export function createFromTemplate(templateId, overrides = {}) {
    const template = templateMap.get(templateId);
    if (!template) {
        throw new Error(`Unknown template: "${templateId}"`);
    }

    const workflow = new Workflow();
    const warnings = [];
    const nodeOverrides = overrides.nodeOverrides || {};

    // Pre-validate: check all template actions exist before creating any nodes
    const missingActions = template.steps
        .filter((step) => !getAction(step.actionId))
        .map((step) => step.actionId);
    if (missingActions.length > 0) {
        warnings.push(
            `Template "${templateId}" references ${missingActions.length} action(s) not in registry: `
            + `${missingActions.join(', ')}. These steps will be created but may not execute.`
        );
    }

    // Map step index → created node ID
    const stepNodeIds = [];

    for (let i = 0; i < template.steps.length; i++) {
        const step = template.steps[i];
        const nodeKey = `node-${i + 1}`;
        const stepOverride = nodeOverrides[nodeKey] || {};

        // Merge paramDefaults with any caller overrides
        const paramOverrides = {
            ...(step.paramDefaults || {}),
            ...(stepOverride.paramOverrides || {}),
        };

        const label = stepOverride.label || step.label;

        try {
            const node = workflow.addNode(step.actionId, {
                label,
                paramOverrides,
                position: step.position,
            });
            stepNodeIds.push(node.id);
        } catch (err) {
            // If addNode throws (e.g., unknown actionId), record warning
            // and push null so connection indices still align
            warnings.push(`Failed to add node for "${step.actionId}": ${err.message}`);
            stepNodeIds.push(null);
        }
    }

    // Wire connections
    for (const conn of template.connections) {
        const sourceId = stepNodeIds[conn.fromStep];
        const targetId = stepNodeIds[conn.toStep];
        if (!sourceId || !targetId) {
            warnings.push(
                `Skipped connection (step ${conn.fromStep} → step ${conn.toStep}): `
                + 'one or both nodes failed to create'
            );
            continue;
        }
        try {
            workflow.addEdge(sourceId, targetId, conn.outputName, conn.inputName);
        } catch (err) {
            warnings.push(`Failed to add edge (${conn.outputName} → ${conn.inputName}): ${err.message}`);
        }
    }

    return { workflow, warnings };
}

/**
 * Build a linear workflow from an ordered array of action IDs.
 *
 * Each action is connected to the next by auto-mapping the first compatible
 * output→input pair (using ActionDef type metadata). If no compatible pair
 * is found between two adjacent actions, the edge is created with empty
 * output/input names and a warning is emitted.
 *
 * @param {string[]} actionIds - Ordered list of action IDs
 * @returns {{ workflow: Workflow, warnings: string[] }}
 * @throws {Error} If any actionId is not found in registry
 */
export function createFromSequence(actionIds) {
    if (!actionIds || actionIds.length === 0) {
        throw new Error('actionIds array must not be empty');
    }

    const workflow = new Workflow();
    const warnings = [];
    const nodeIds = [];

    // Create all nodes
    for (const actionId of actionIds) {
        const node = workflow.addNode(actionId);
        nodeIds.push(node.id);
    }

    // Connect adjacent pairs by finding compatible output→input
    for (let i = 0; i < nodeIds.length - 1; i++) {
        const sourceAction = getAction(actionIds[i]);
        const targetAction = getAction(actionIds[i + 1]);

        const mapping = findCompatibleMapping(sourceAction, targetAction);
        if (mapping) {
            workflow.addEdge(nodeIds[i], nodeIds[i + 1], mapping.outputName, mapping.inputName);
        } else {
            warnings.push(
                `No compatible output→input between "${actionIds[i]}" and "${actionIds[i + 1]}". `
                + 'Edge created with empty mapping.'
            );
            workflow.addEdge(nodeIds[i], nodeIds[i + 1], '', '');
        }
    }

    return { workflow, warnings };
}

/**
 * Find the first compatible output→input mapping between two actions.
 * Checks for exact type name matches between source outputs and target inputs.
 *
 * @param {Object} sourceAction - Source ActionDef
 * @param {Object} targetAction - Target ActionDef
 * @returns {{ outputName: string, inputName: string } | null}
 * @private
 */
function findCompatibleMapping(sourceAction, targetAction) {
    if (!sourceAction || !targetAction) return null;

    for (const output of sourceAction.outputs) {
        for (const input of targetAction.inputs) {
            // Direct type match or Compound→CompoundList / CompoundList→Compound compatibility
            if (
                output.type === input.type
                || (output.type === 'Compound' && input.type === 'CompoundList')
                || (output.type === 'CompoundList' && input.type === 'Compound')
                || (output.type === 'CaseData' && input.type === 'CaseData')
            ) {
                return { outputName: output.name, inputName: input.name };
            }
        }
    }
    return null;
}
